import { parseIntelligenceContext } from '../contracts/validation.ts';
import type { IntelligenceContext, IntelligenceRequest } from '../contracts/ccos';
import { CCOS_CONTRACT_VERSION, shellEngineIds } from '../contracts/ccos.ts';
import type { ProductId } from '../contracts/index';
import type { ValidationResult } from '../contracts/schema';
import { validateContext, validateRequestContext, type ContextCatalog, type ContextValidation } from './validation.ts';

const record = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value) && [Object.prototype, null].includes(Object.getPrototypeOf(value));

/** RC1 display labels/season strings -> canonical wire values. No access grants copied. */
export function contextFromRC1(input: unknown): ValidationResult<IntelligenceContext> {
  if (!record(input) || !record(input.userContext) || !record(input.programContext) ||
      !(input.coachContext === null || record(input.coachContext)) ||
      !(input.athleteContext === undefined || record(input.athleteContext))) {
    return { ok: false, issues: [{ path: '$', code: 'invalid_shell_context', message: 'Expected RC1 user/program objects, explicit coach object or null, and optional athlete object.' }] };
  }
  const program = input.programContext;
  const athlete = input.athleteContext;
  if (athlete && (typeof athlete.id !== 'string' || typeof athlete.position !== 'string')) {
    return { ok: false, issues: [{ path: '$.athleteContext', code: 'invalid_shell_athlete', message: 'RC1 athlete requires an ID and position.' }] };
  }
  return parseIntelligenceContext({
    user_id: input.userContext.id,
    program_id: program.id,
    sport: program.sport === 'Football' ? 'football' : program.sport === 'Basketball' ? 'basketball' : undefined,
    season: typeof program.season === 'string' && /^\d{4}$/.test(program.season) ? Number(program.season) : undefined,
    coach_id: input.coachContext === null ? null : input.coachContext.id,
    ...(athlete ? { athlete_id: athlete.id, position: athlete.position } : {})
  });
}

export function validateRC1Context(input: unknown, catalog: ContextCatalog): ContextValidation {
  const adapted = contextFromRC1(input);
  return adapted.ok ? validateContext(adapted.value, catalog) : adapted;
}

/** Builds a validated DTO only: no network, job creation, registry or authorization. */
export function requestFromRC1(input: unknown, product: ProductId, requestId: string, requestedFeatures: readonly string[], catalog: ContextCatalog): ContextValidation<IntelligenceRequest> {
  const adapted = validateRC1Context(input, catalog);
  if (!adapted.ok) return adapted;
  const request = validateRequestContext({
    contract_version: CCOS_CONTRACT_VERSION, request_id: requestId,
    engine_id: Object.hasOwn(shellEngineIds, product) ? shellEngineIds[product] : undefined,
    context: adapted.value, requested_features: requestedFeatures
  }, catalog);
  return request.ok ? { ...request, warnings: [...adapted.warnings, ...request.warnings] } : request;
}
